import { Baby } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { usePublicSharePregnancies } from '../api/public-share-api';
import type { PublicPregnancy } from '../types';
import { formatIsoDate } from '../utils/format';
import { PublicSection } from './PublicSection';

function statusVariant(status: string | null): 'default' | 'secondary' | 'outline' {
  if (status === 'active') return 'default';
  if (status === 'delivered') return 'secondary';
  return 'outline';
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function PregnancyCard({ p }: { p: PublicPregnancy }) {
  const edd = formatIsoDate(p.edd);
  const lmp = formatIsoDate(p.lmp);
  return (
    <div className="rounded border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {p.status && (
            <Badge variant={statusVariant(p.status)} className="capitalize">
              {p.status}
            </Badge>
          )}
          {p.vbac && <Badge variant="outline" className="text-xs">VBAC</Badge>}
        </div>
        {p.gtpal && (
          <span className="font-mono text-xs text-muted-foreground">{p.gtpal}</span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        {edd && <DetailRow label="EDD" value={edd} />}
        {lmp && <DetailRow label="LMP" value={lmp} />}
        {p.edd_method && <DetailRow label="EDD method" value={p.edd_method} />}
        {p.fetus_count !== null && p.fetus_count > 0 && (
          <DetailRow label="Fetuses" value={p.fetus_count} />
        )}
        {p.gravida !== null && <DetailRow label="Gravida" value={p.gravida} />}
        {p.term !== null && <DetailRow label="Term" value={p.term} />}
        {p.preterm !== null && <DetailRow label="Preterm" value={p.preterm} />}
        {p.abortions !== null && <DetailRow label="Abortions" value={p.abortions} />}
        {p.living !== null && <DetailRow label="Living" value={p.living} />}
      </div>
      {p.problems && (
        <div className="rounded bg-muted/40 p-2 text-xs text-muted-foreground">
          <span className="font-semibold">Problems:</span> {p.problems}
        </div>
      )}
    </div>
  );
}

export function PublicPregnanciesList({ token }: { token: string }) {
  const { data, isLoading } = usePublicSharePregnancies(token, true);
  const rows = data ?? [];
  return (
    <PublicSection
      title="Pregnancies"
      icon={Baby}
      count={rows.length}
      isLoading={isLoading}
      emptyMessage="No pregnancies to show."
    >
      <div className="space-y-3">
        {rows.map((p) => (
          <PregnancyCard key={p.id} p={p} />
        ))}
      </div>
    </PublicSection>
  );
}
